/**
 * 进房：6 个大格子的房间码输入。
 *
 * 这一屏是朋友的**第一次真实交互**——他刚从微信里拿到 6 个字符，切到浏览器，
 * 打开站点。这里每多一步操作都会掉一批人，所以：
 *   - 自动聚焦第一格，输满 6 位**自动提交**，不用再找按钮；
 *   - 自动大写、自动去空格与横杠、粘贴自动填充并丢掉多余字符；
 *   - 输入非法字符（O / I / L / 0 / 1 …）时给**具体**提示（「你看到的多半是 Q 或 D」），
 *     而不是把它默默吞掉让人对着屏幕发呆；
 *   - 失败提示区分「房间不存在 / 房间满了 / 房间已结束」，不出现「加入失败」。
 *
 * 草稿码存在 `useLobbyFlow.lastCode` 而不是组件 state：stage 与 dock 是两个平行 slot，
 * dock 上的「加入」按钮要读同一份草稿。
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ClipboardEvent, KeyboardEvent, ReactNode } from 'react'

import { BackRow } from './CreateRoom.js'
import { useLobbyFlow } from './lobbyFlow.js'
import { IconAlert } from './lobbyIcons.js'
import { acceptCodeInput, codeCells, ROOM_CODE_LENGTH } from './roomCode.js'
import { useNicknameStore } from './useAuth.js'
import { vibrate } from './env.js'
import './lobby.css'

// ============================================================================
// 6 格输入
// ============================================================================

export interface CodeInputProps {
  readonly value: string
  onChange(next: string): void
  /** 满 6 位时回调一次（自动提交） */
  onComplete?(code: string): void
  readonly invalid?: boolean
  readonly disabled?: boolean
  readonly autoFocus?: boolean
}

/**
 * 聚焦某一格时的落点：不允许中间留空，点到已填区之后的空格子就跳回第一个空位。
 *
 * ★ 抽成纯函数不是为了复用，是为了**把「用哪个长度判断」这件事摆到台面上**。
 *   见 `valueRef` 处的注释：这里传进来的必须是「本次事件已提交的最新长度」，
 *   传渲染闭包里的旧长度会让每次输入都被弹回第 0 格。
 *
 * @returns 要改聚焦到的格子号；null = 留在原地（只需选中本格已有字符）
 */
export function focusTargetOnFocus(index: number, length: number): number | null {
  return index > length ? Math.min(length, ROOM_CODE_LENGTH - 1) : null
}

/** 一次输入事件的结果。`value` 与传入相同 = 这次输入没被接受（非法字符 / 空串）。 */
export interface CodeKeystroke {
  readonly value: string
  /** 输入后焦点该落在哪一格 */
  readonly focus: number
  /** 非 null = 要给用户看的「这个字符不合法」提示 */
  readonly hint: string | null
}

/**
 * 往第 `index` 格输入 `raw` 之后的新码与新焦点。**纯函数**，好让「输入六位」
 * 这条主路径能在没有 DOM 的环境里回归（client 包的 vitest 跑在 node）。
 */
export function applyCodeKeystroke(value: string, index: number, raw: string): CodeKeystroke {
  // 输入法可能一次吐出多个字符（粘贴、联想），全部接进来
  const at = Math.min(index, value.length)
  const head = value.slice(0, at)
  const tail = value.slice(at + 1)
  // 格子里本来就有字、用户又没选中它时，浏览器给的是「旧字 + 新字」；去掉旧字那一份，
  // 否则一次输入会把后面的字符整体往后挤
  const existing = value[at] ?? ''
  const typed = existing !== '' && raw.length > 1 && raw.startsWith(existing) ? raw.slice(existing.length) : raw
  const result = acceptCodeInput(typed, '')
  if (result.code === '') return { value, focus: at, hint: result.hint }
  return {
    value: (head + result.code + tail).slice(0, ROOM_CODE_LENGTH),
    focus: Math.min(at + result.code.length, ROOM_CODE_LENGTH - 1),
    hint: result.hint,
  }
}

export function CodeInput({
  value,
  onChange,
  onComplete,
  invalid = false,
  disabled = false,
  autoFocus = true,
}: CodeInputProps): ReactNode {
  const refs = useRef<(HTMLInputElement | null)[]>([])
  const [hint, setHint] = useState<string | null>(null)
  const cells = codeCells(value)

  /**
   * ★ 当前码的「最新值」。**不能只靠渲染闭包里的 `value`**。
   *
   * 症状（修复前 100% 复现，与打字快慢无关）：每输入一个字符，焦点会被弹回第 0 格
   * 并把已有字符选中，于是下一个字符**替换**掉它 —— 六位码永远输不满，
   * 朋友根本进不了房间。
   *
   * 成因：`typeInto` 里 `commit()` 只是把新值排进 React 的更新队列，
   * 紧接着的 `focusCell(at + 1)` 会**同步**触发下一格的 focus 事件，
   * 而那一格的 `onFocus` 闭包仍然属于本次渲染（`value` 还是旧的）：
   * 输入第 1 个字符后 `i=1 > value.length=0` 成立 → 判成「中间留空」→ 弹回第 0 格。
   * React 的事件内批量更新保证了这个顺序，所以慢慢打字也躲不掉。
   *
   * 解法：值一提交就先写进 ref，所有**同步**读取当前值的地方（focus 守卫、
   * 同一 tick 内的连续输入）一律读 ref，渲染仍然用 props 的 `value`。
   */
  const valueRef = useRef(value)
  valueRef.current = value

  const focusCell = useCallback((i: number): void => {
    const clamped = Math.max(0, Math.min(ROOM_CODE_LENGTH - 1, i))
    const el = refs.current[clamped]
    el?.focus()
    // 已有字符时选中它：再打一个字直接替换，不用先退格
    el?.select?.()
  }, [])

  // 自动聚焦第一格。iOS 上不一定弹出键盘（需要用户手势），但光标位置的视觉提示仍然有用。
  useEffect(() => {
    if (autoFocus && !disabled) focusCell(value.length)
    // 只在挂载时做一次：输入过程中再抢焦点会把用户拽回去
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const commit = useCallback(
    (next: string): void => {
      // ★ 先落 ref 再通知外部：紧随其后的同步 focus 事件要靠它判断「有没有中间留空」
      valueRef.current = next
      onChange(next)
      if (next.length === ROOM_CODE_LENGTH) {
        // 满 6 位：收起键盘再自动提交，否则键盘会把「正在进入」的提示挡住
        refs.current[ROOM_CODE_LENGTH - 1]?.blur()
        onComplete?.(next)
      }
    },
    [onChange, onComplete],
  )

  const typeInto = useCallback(
    (index: number, raw: string): void => {
      // ★ 读 ref 而不是渲染闭包：同一 tick 里连打两个字符时闭包里的 value 还是上一次的
      const step = applyCodeKeystroke(valueRef.current, index, raw)
      if (step.hint !== null) {
        setHint(step.hint)
        vibrate(12)
      } else if (step.value !== valueRef.current) {
        setHint(null)
      }
      if (step.value === valueRef.current) return
      commit(step.value)
      focusCell(step.focus)
    },
    [commit, focusCell],
  )

  const onKeyDown = useCallback(
    (index: number, e: KeyboardEvent<HTMLInputElement>): void => {
      const value = valueRef.current
      if (e.key === 'Backspace') {
        e.preventDefault()
        if (value.length === 0) return
        // 当前格有字就删当前格，空格子就退到上一格删——这是所有分段输入框的通行手感
        const target = index < value.length ? index : value.length - 1
        commit(value.slice(0, target) + value.slice(target + 1))
        focusCell(target)
        setHint(null)
        return
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        focusCell(index - 1)
        return
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        focusCell(index + 1)
      }
    },
    [commit, focusCell],
  )

  const onPaste = useCallback(
    (e: ClipboardEvent<HTMLInputElement>): void => {
      e.preventDefault()
      const text = e.clipboardData.getData('text')
      const result = acceptCodeInput(text, '')
      setHint(result.hint)
      if (result.code === '') return
      commit(result.code)
      focusCell(result.code.length)
    },
    [commit, focusCell],
  )

  return (
    <div>
      <div
        className={invalid ? 'codebox codebox--error' : 'codebox'}
        role="group"
        aria-label={`${String(ROOM_CODE_LENGTH)} 位房间码`}
      >
        {cells.map((ch, i) => (
          <input
            // 位置就是身份：这个列表永远是 6 个格子，不会重排
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            ref={(el) => {
              refs.current[i] = el
            }}
            className={ch === '' ? 'codecell' : 'codecell codecell--filled'}
            value={ch}
            disabled={disabled}
            inputMode="text"
            autoCapitalize="characters"
            autoCorrect="off"
            autoComplete="off"
            spellCheck={false}
            maxLength={2}
            aria-label={`房间码第 ${String(i + 1)} 位`}
            onChange={(e) => typeInto(i, e.target.value)}
            onKeyDown={(e) => onKeyDown(i, e)}
            onPaste={onPaste}
            onFocus={(e) => {
              // 不允许中间留空：点到后面的空格子时跳回第一个空位。
              // ★ 必须用 valueRef（本次事件刚提交的值），用渲染闭包里的 value 会把
              //   「刚输入完自动前移」误判成中间留空，焦点被弹回第 0 格（见上方注释）
              const target = focusTargetOnFocus(i, valueRef.current.length)
              if (target !== null) {
                focusCell(target)
                return
              }
              // 选中已有字符：再打一个字直接替换，省掉一次退格
              e.currentTarget.select()
            }}
          />
        ))}
      </div>
      {/* 只在输错字符时说话：「码里没有哪些字符」是实现细节，没人会在输入前读它。
          这一行常驻占位（min-height）而不是按需出现，免得提示一冒出来就把下面的内容顶下去。 */}
      <p
        className="lobby-note"
        style={{ marginTop: 'var(--sp-2)', minHeight: 'calc(var(--fs-sm) * var(--lh-base))' }}
        aria-live="polite"
      >
        {hint}
      </p>
    </div>
  )
}

// ============================================================================
// 进房屏
// ============================================================================

export interface JoinRoomProps {
  /** 覆盖昵称来源（默认读本地昵称） */
  readonly nickname?: string
  readonly autoFocus?: boolean
  /** 顶部提示条（微信提醒等）。由 `Lobby` 决定要不要给。 */
  readonly banner?: ReactNode
}

/** 提交一次（自动或手动）。抽出来是因为 stage 的自动提交和 dock 的按钮要走同一条路。 */
function useJoinSubmit(nickname: string): (code: string) => void {
  const submitJoin = useLobbyFlow((s) => s.submitJoin)
  const lastAttempt = useRef<string>('')
  return useCallback(
    (code: string) => {
      // 同一个码被服务端拒过之后不再自动重发：否则「房间不存在」会变成无限重试
      if (code === lastAttempt.current) return
      lastAttempt.current = code
      submitJoin(code, nickname)
    },
    [nickname, submitJoin],
  )
}

export function JoinRoom({ nickname, autoFocus = true, banner }: JoinRoomProps = {}): ReactNode {
  const storedNickname = useNicknameStore((s) => s.nickname)
  const name = nickname ?? storedNickname
  const code = useLobbyFlow((s) => s.lastCode)
  const setLastCode = useLobbyFlow((s) => s.setLastCode)
  const pending = useLobbyFlow((s) => s.pending)
  const error = useLobbyFlow((s) => s.error)
  const clearError = useLobbyFlow((s) => s.clearError)
  const go = useLobbyFlow((s) => s.go)
  const submit = useJoinSubmit(name)

  const onChange = useCallback(
    (next: string) => {
      setLastCode(next)
      if (error !== null) clearError()
    },
    [clearError, error, setLastCode],
  )

  const joining = pending?.kind === 'join'

  return (
    <div className="lobby">
      {banner}
      <BackRow onBack={() => go('home')} />

      {/* ★ 标题 + 6 格输入是这一屏的**唯一主角**，整组垂直居中（实测修复）。
          旧版把它钉在顶部、只在下面挂一条 flex:1 的气口：440×856 上量到
          409px（占舞台 48%）的一整块空黑，码格却挤在屏幕最上沿、离拇指最远。
          现在剩余高度由这个容器吃掉并把内容居中，空间用在「让码格离手更近」上。 */}
      <div className="join__main">
        {/* 标题就是全部说明：大小写自动处理、输满自动提交，这些做了就行，不用汇报 */}
        <div className="lobby__hero">
          <h1 className="lobby__title">输入房间码</h1>
        </div>

        <CodeInput
          value={code}
          onChange={onChange}
          onComplete={submit}
          invalid={error !== null}
          disabled={joining}
          autoFocus={autoFocus}
        />

        {/* 失败必须说清「为什么」：describeRoomError 已经把错误码翻成了具体说法，
            绝不出现「加入失败」这种让人无从下手的文案（P5）。 */}
        {error !== null && (
          <div className="lobby-error" role="alert">
            <IconAlert size={18} />
            <span>{error}</span>
          </div>
        )}
        {/* 「正在加入…」dock 上那颗按钮已经写着了，同一屏不再写第二遍；
            自动提交时手指不在按钮上，所以留一条只给屏幕阅读器的播报 */}
        {joining && (
          <p className="sr-only" aria-live="polite">
            正在加入…
          </p>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// dock 区 CTA
// ============================================================================

export interface JoinRoomDockProps extends JoinRoomProps {
  onBack?(): void
}

export function JoinRoomDock({ nickname }: JoinRoomDockProps = {}): ReactNode {
  const storedNickname = useNicknameStore((s) => s.nickname)
  const name = nickname ?? storedNickname
  const code = useLobbyFlow((s) => s.lastCode)
  const pending = useLobbyFlow((s) => s.pending)
  const submitJoin = useLobbyFlow((s) => s.submitJoin)
  const joining = pending?.kind === 'join'
  const missing = ROOM_CODE_LENGTH - code.length
  const ready = missing <= 0 && !joining

  // 「返回」在内容区顶部，dock 只留这一屏唯一的主 CTA
  return (
    <div className="lobby-dock">
      <button
        type="button"
        className="lobby-cta pressable"
        disabled={!ready}
        onClick={() => submitJoin(code, name)}
      >
        {joining ? '正在加入…' : '加入房间'}
      </button>
      {/* 按不动时说明还差什么，而不是让人对着灰按钮发呆（P5） */}
      {!ready && !joining && (
        <p className="lobby-dock__say" role="status">
          还差 <span className="num">{missing}</span> 位
        </p>
      )}
    </div>
  )
}

export default JoinRoom
